import React, { useState, useEffect, useMemo, useRef } from 'react';

/**
 * Page publique d'un formulaire — erpia.ch/formulaire/:token
 *
 * La personne qui la remplit n'a pas de compte, ne connaît pas le produit, le
 * fait une fois, souvent depuis son téléphone. L'écran est donc l'inverse de
 * la densité interne : une colonne, des champs larges, et un seul décompte —
 * ce qu'il reste à renseigner. Pas d'étapes : neuf questions ne se découpent
 * pas en parcours.
 *
 * Routes SANS auth :
 *   GET  /public/formulaire/:token → { champs: [{ cle, libelle, type? }] }
 *                                    409 si le formulaire est déjà rempli
 *   POST /public/formulaire/:token → { donnees: {…} } ; 400 + champ si refus
 */

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon }) => <i className={`bi bi-${icon}`} aria-hidden="true" />;
/* Les champs de données se lisent en mono : un IBAN ou un n° AVS se relit
   caractère par caractère, pas en typographie de texte courant. */
const MONO = /avs|iban|npa|numero|num_/i;
const AUTO = { adresse: 'street-address', npa: 'postal-code', ville: 'address-level2', email: 'email', tel: 'tel', date_naissance: 'bday', nationalite: 'country-name' };

export default function PublicFormulaire({ token }) {
  const [etat, setEtat] = useState('chargement');   // chargement | pret | deja | invalide | envoye
  const [data, setData] = useState(null);
  const [valeurs, setValeurs] = useState({});
  const [erreur, setErreur] = useState('');
  const [champErreur, setChampErreur] = useState(null);   // { champ, message }
  const [busy, setBusy] = useState(false);
  const refs = useRef({});

  useEffect(() => {
    let annule = false;
    (async () => {
      try {
        const r = await fetch(`${BASE}/public/formulaire/${encodeURIComponent(token)}`);
        const j = await r.json().catch(() => ({}));
        if (annule) return;
        if (r.status === 409) { setData(j.data || j); setEtat('deja'); return; }
        if (!r.ok) { setErreur(j.error || j.message || ''); setEtat('invalide'); return; }
        const d = j.data || j;
        const champs = Array.isArray(d.champs) ? d.champs.filter(c => c && c.cle) : [];
        if (!champs.length) { setEtat('invalide'); return; }
        setData({ ...d, champs });
        setValeurs(Object.fromEntries(champs.map(c => [c.cle, d.donnees?.[c.cle] ?? ''])));
        setEtat('pret');
      } catch { if (!annule) setEtat('invalide'); }
    })();
    return () => { annule = true; };
  }, [token]);

  const champs = useMemo(() => data?.champs || [], [data]);
  const obligatoires = useMemo(() => champs.filter(c => c.obligatoire !== false), [champs]);
  const restants = obligatoires.filter(c => !String(valeurs[c.cle] || '').trim()).length;

  const envoyer = async () => {
    setBusy(true); setChampErreur(null); setErreur('');
    try {
      const donnees = Object.fromEntries(
        champs.map(c => [c.cle, String(valeurs[c.cle] ?? '').trim()]).filter(([, v]) => v !== ''),
      );
      const r = await fetch(`${BASE}/public/formulaire/${encodeURIComponent(token)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ donnees }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.status === 400) {
        const msg = j.error || j.message || 'Une information n\'a pas été acceptée.';
        // Le serveur nomme le champ fautif : on y emmène la personne plutôt
        // que de lui laisser chercher lequel des neuf pose problème.
        if (j.champ) {
          setChampErreur({ champ: j.champ, message: msg });
          const el = refs.current[j.champ];
          if (el) { el.focus(); el.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
        } else setErreur(msg);
        return;
      }
      if (r.status === 409) { setEtat('deja'); return; }
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || 'Envoi impossible.');
      setData(d => ({ ...d, message: j.message }));
      setEtat('envoye');
    } catch (e) { setErreur(e.message); }
    finally { setBusy(false); }
  };

  const org = data?.organisation || data?.agence || '';
  const cadre = (icone, titre, texte) => (
    <main className="pubform">
      <div className="pubform-carte pubform-carte--message">
        <BI icon={icone} />
        <h1>{titre}</h1>
        <p>{texte}</p>
      </div>
    </main>
  );

  if (etat === 'chargement') return cadre('arrow-repeat', 'Ouverture du formulaire…', 'Un instant.');
  if (etat === 'invalide') return cadre('link-45deg', 'Ce lien n\'est plus valable',
    <>{erreur && <><b>{erreur}</b><br /></>}Il a peut-être été remplacé par un lien plus récent. Demandez-en un nouveau à la personne qui vous l'a envoyé.</>);
  if (etat === 'deja') return cadre('check-circle', 'C\'est déjà fait',
    data?.message || 'Vos informations ont bien été reçues. Vous n\'avez rien d\'autre à faire.');
  if (etat === 'envoye') return cadre('check-circle-fill', 'Merci, c\'est envoyé',
    data?.message || `Vos informations sont transmises${org ? ` à ${org}` : ''}. Votre contrat sera établi avec.`);

  return (
    <main className="pubform">
      <div className="pubform-carte">
        <header className="pubform-tete">
          {org && <p className="pubform-kicker">{org}</p>}
          <h1>{data.nom || 'Vos informations'}</h1>
          <p>{data.description || 'Ces informations figureront sur votre contrat. Prenez le temps de les vérifier — vous ne remplirez ce formulaire qu\'une fois.'}</p>
        </header>

        {erreur && <div className="pubform-erreur" role="alert">{erreur}</div>}

        <div className="pubform-champs">
          {champs.map((c) => {
            const faux = champErreur?.champ === c.cle;
            const type = c.type === 'texte_long' ? 'textarea' : c.type === 'choix' ? 'choix' : c.type || 'texte';
            const commun = {
              id: `f-${c.cle}`, value: valeurs[c.cle] || '',
              'aria-invalid': faux || undefined,
              'aria-describedby': faux ? `e-${c.cle}` : undefined,
              autoComplete: AUTO[c.cle] || 'on',
              ref: (el) => { refs.current[c.cle] = el; },
              onChange: (e) => { const v = e.target.value; setValeurs(p => ({ ...p, [c.cle]: v })); if (faux) setChampErreur(null); },
            };
            return (
              <label className="pubform-champ" key={c.cle} htmlFor={`f-${c.cle}`}>
                <span className="pubform-label">
                  {c.libelle || c.cle}
                  {c.obligatoire === false && <em>facultatif</em>}
                </span>
                {type === 'textarea' ? (
                  <textarea {...commun} rows={4} className={faux ? 'is-faux' : undefined} />
                ) : type === 'choix' ? (
                  <select {...commun} className={faux ? 'is-faux' : undefined}>
                    <option value="">Choisissez…</option>
                    {(c.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input {...commun}
                    type={['date', 'email', 'tel', 'number'].includes(type) ? (type === 'nombre' ? 'number' : type) : 'text'}
                    inputMode={c.cle === 'npa' ? 'numeric' : undefined}
                    className={`${MONO.test(c.cle) ? 'pubform-mono' : ''}${faux ? ' is-faux' : ''}`.trim() || undefined} />
                )}
                {faux && <span className="pubform-champ-erreur" id={`e-${c.cle}`} role="alert">{champErreur.message}</span>}
              </label>
            );
          })}
        </div>
      </div>

      {/* Ce qu'il reste, pas un pourcentage : « 82 % » ne dit pas quoi faire. */}
      <div className="pubform-pied">
        <span className="pubform-reste">
          {restants === 0 ? 'Tout est renseigné.' : `Il reste ${restants} information${restants > 1 ? 's' : ''} à renseigner.`}
        </span>
        <button type="button" className="pubform-envoyer" onClick={envoyer} disabled={busy || restants > 0}>
          {busy ? 'Envoi…' : 'Envoyer mes informations'}
        </button>
      </div>
    </main>
  );
}
